<script>
  import { onMount, onDestroy } from 'svelte';
  import { browser } from '$app/environment';
  import { convexQuery } from '$lib/convex.js';
  import { goto } from '$app/navigation';

  let searchQuery = '';
  /** @type {any[]} */
  let searchResults = [];
  let isLoading = false;
  let showDropdown = false;
  /** @type {any} */
  let debounceTimer = null;
  /** @type {string | null} */
  let error = null;
  
  const DEBOUNCE_DELAY = 500; // 500ms pulse delay

  /**
   * Debounced search function
   */
  async function performSearch() {
    // Only run in browser
    if (!browser) return;
    
    if (!searchQuery.trim()) {
      searchResults = [];
      showDropdown = false;
      return;
    }

    isLoading = true;
    error = null;

    try {
      const results = await convexQuery('functions/user/search:searchProfiles', {
        q: searchQuery,
        limit: 10, // Limit dropdown results
      });
      
      searchResults = results.results || [];
      console.log(results)
      showDropdown = searchResults.length > 0;
    } catch (err) {
      console.error('Search error:', err);
      error = 'Failed to load results';
      searchResults = [];
      showDropdown = false;
    } finally {
      isLoading = false;
    }
  }

  /**
   * Handle input changes with debouncing
   */
  function handleInput() {
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer
    debounceTimer = setTimeout(() => {
      performSearch();
    }, DEBOUNCE_DELAY);
  }

  /**
   * Handle profile selection
   * @param {any} profile
   */
  function selectProfile(profile) {
    // Navigate to profile page
    if (browser) {
      window.location.href = `https://app.addmeetings.com/profile/${profile.slug}`;
    }
  }

  /**
   * Navigate to full results page
   */
  function viewAllResults() {
    if (searchQuery.trim()) {
      goto(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  }

  /**
   * Handle Enter key press
   * @param {KeyboardEvent} event
   */
  function handleKeydown(event) {
    if (event.key === 'Enter' && searchQuery.trim()) {
      viewAllResults();
    }
  }

  /**
   * Close dropdown when clicking outside
   * @param {MouseEvent} event
   */
  function handleClickOutside(event) {
    const target = event.target;
    if (target && target instanceof Element && !target.closest('.search-container')) {
      showDropdown = false;
    }
  }

  onMount(() => {
    if (browser) {
      document.addEventListener('click', handleClickOutside);
    }
  });

  onDestroy(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (browser) {
      document.removeEventListener('click', handleClickOutside);
    }
  });
</script>

<div class="search-container relative w-full">
  <div class="w-full px-3 border-2 border-zinc-400 rounded-lg bg-none flex items-center gap-3 focus-within:border-zinc-900 transition-all">
    <input 
      type="text"
      class="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-0 border-none"
      placeholder="Find meeting profiles..."
      bind:value={searchQuery}
      on:input={handleInput}
      on:keydown={handleKeydown}
    />
    {#if isLoading}
      <div class="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
    {:else}
      <button 
        type="button"
        class="material-symbols-rounded text-gray-600 cursor-pointer bg-transparent border-0 p-0"
        on:click={viewAllResults}
        aria-label="Search"
      >
        data_loss_prevention
      </button>
    {/if}
  </div>

  {#if showDropdown && searchResults.length > 0}
    <div class="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto">
      {#each searchResults as profile (profile.id)}
        <button
          class="w-full px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-left"
          on:click={() => selectProfile(profile)}
        >
          <div class="flex items-start gap-3">
            {#if profile.avatar}
              <img 
                src={profile.avatar} 
                alt={profile.name}
                class="w-12 h-12 rounded-full object-cover"
              />
            {:else}
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                {profile.name.charAt(0).toUpperCase()}
              </div>
            {/if}
            
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <h3 class="font-semibold text-gray-900 truncate">{profile.name}</h3>
                {#if profile.verified}
                  <span class="text-blue-500" title="Verified">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                  </span>
                {/if}
              </div>
              
              {#if profile.title}
                <p class="text-sm text-gray-600 truncate">{profile.title}</p>
              {/if}
              
              <div class="flex items-center gap-2 mt-1 text-xs text-gray-500">
                {#if profile.country}
                  <span>{profile.country}</span>
                {/if}
                {#if profile.price}
                  <span>•</span>
                  <span class="font-medium">Credits: {profile.price}/{profile.duration}min</span>
                {/if}
              </div>
            </div>
          </div>
        </button>
      {/each}
      
      {#if searchResults.length >= 10}
        <button
          class="w-full px-4 py-3 text-center text-blue-600 hover:bg-blue-50 font-medium transition-colors"
          on:click={viewAllResults}
        >
          View all results →
        </button>
      {/if}
    </div>
  {/if}

  {#if error}
    <div class="absolute z-50 w-full mt-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
      {error}
    </div>
  {/if}
</div>

<style>
  :global(body) {
    margin: 0;
    font-family:
      system-ui,
      -apple-system,
      sans-serif;
  }

  .search-container {
    position: relative;
  }
</style>
